// The option controls of MODDING (R1909, ui-map-interface.md §16.3, card
// 01 of mockup round 092): the markup of THE WHOLE LOOK, of the option
// blocks of THEMES and of the slot map and WHAT FITS of EFFECTS, and the
// fill of the pictures. A look is a preview tile of the choice kit
// (R2011), and a sound is a row of a pick list with ▶.
//
// Every builder is pure: it reads no state and returns markup. Every press
// is a `<button>` that carries `data-act`, so the hub routes it on the
// release (R1206) and the keyboard reaches it. A picture is an empty
// `[data-pic]` box in the markup, and `fillPictures` puts the image in
// after a rewrite and after a bake. The markup therefore never changes
// when a picture is baked, and a bake never rewrites the pane.

import { VENDORED_THEMES } from '../../../gen/presentation';
import { hubMods as S } from '../../../gen/strings';
import type { Tile } from '../../choice/choice';
import { PLAN_WORDS, esc, readable } from '../panel';
import { pictureUrl } from './mods-pictures';

/** The vendored levels of WORLD, MARKS and CHART, from quiet to loud. A
 *  level the table does not name stands after them, in the catalog order. */
const LOUDNESS: readonly string[] = ['minimal', 'auto', 'flashy'];

/** The words of the three named levels. A level reads its own word, so the
 *  table is a `Map` (R1341). */
const LEVEL_WORDS: ReadonlyMap<string, string> = new Map([
  ['minimal', S.level_minimal],
  ['auto', S.level_auto],
  ['flashy', S.level_flashy],
]);

/** The word of one level on THE WHOLE LOOK. */
export function levelWord(id: string): string {
  return LEVEL_WORDS.get(id) ?? readable(id);
}

/** `list` from quiet to loud. The sort is stable, so an unknown id keeps
 *  the catalog order. */
export function byLoudness<T extends { id: string }>(list: readonly T[]): T[] {
  const rank = (id: string): number => {
    const at = LOUDNESS.indexOf(id);
    return at < 0 ? LOUDNESS.length : at;
  };
  return [...list].sort((a, b) => rank(a.id) - rank(b.id));
}

/** The categories THE WHOLE LOOK sets together. */
export const WHOLE_CATS = ['world', 'marks', 'chart'] as const;

/** The levels of THE WHOLE LOOK: the vendored ids the three categories
 *  share, from quiet to loud. Each one is free (R1231). */
export function wholeLevels(): string[] {
  const sets = WHOLE_CATS.map((c) => new Set<string>(VENDORED_THEMES[c].map((e) => e.id)));
  const ids = VENDORED_THEMES.world.map((e) => e.id).filter((id) => sets.every((s) => s.has(id)));
  return byLoudness(ids.map((id) => ({ id }))).map((x) => x.id);
}

// --- the pictures ------------------------------------------------------------

/** The newest picture shown for one option, while the picture of the look
 *  now is baked: a press does not blank the picture it changes. */
const stale = new Map<string, string>();

/** The empty box of one picture. `option` names the option the picture
 *  shows, whatever look the key holds. */
export function picBox(key: string, option: string): string {
  return `<span class="mpic" data-pic="${esc(key)}" data-for="${esc(option)}"></span>`;
}

/** Puts the baked image into every picture box under `root`, or into the
 *  boxes of one key. A box whose picture is not baked shows the newest
 *  picture of its option, dimmed, or nothing. */
export function fillPictures(root: ParentNode, only?: string): void {
  const sel = only === undefined ? '[data-pic]' : `[data-pic="${only}"]`;
  for (const el of root.querySelectorAll<HTMLElement>(sel)) {
    const key = el.dataset.pic ?? '';
    const option = el.dataset.for ?? '';
    const fresh = pictureUrl(key);
    const url = fresh ?? stale.get(option);
    if (url === undefined) continue;
    if (fresh !== undefined) stale.set(option, fresh);
    let img = el.querySelector('img');
    if (img === null) {
      img = document.createElement('img');
      img.alt = '';
      el.append(img);
    }
    if (img.getAttribute('src') !== url) img.src = url;
    el.classList.toggle('stale', fresh === undefined);
  }
}

/** Drops the stale pictures: for a test. */
export function resetStale(): void {
  stale.clear();
}

// --- the option controls -----------------------------------------------------

/** One option as a control draws it. */
export interface OptionView {
  /** The press and its data. */
  act: string;
  data: Record<string, string>;
  /** The stored pick. */
  on: boolean;
  /** The account does not hold what the option needs: the tier it names. */
  locked: '' | 'supporter' | 'premium';
  name: string;
  /** A second line under the name. */
  sub?: string;
}

function attrs(o: OptionView): string {
  const data = Object.entries(o.data).map(([k, v]) => ` data-${k}="${esc(v)}"`).join('');
  return ` data-act="${esc(o.act)}"${data} aria-pressed="${o.on}"`;
}

function cls(base: string, o: OptionView): string {
  return `${base}${o.on ? ' on' : ''}${o.locked === '' ? '' : ' lk'}`;
}

/** The mark at the end of an option: ✓ on the pick, the tier on a locked
 *  option, nothing else. */
function mark(o: OptionView): string {
  if (o.on) return '<em>✓</em>';
  if (o.locked === '') return '';
  return `<small class="mtier">${PLAN_WORDS[o.locked]}</small>`;
}

/**
 * One option as a preview tile of the choice kit (R1909, R2011): the
 * picture over the name, the tier a locked option needs and a second
 * line. A locked option stays a press: the press previews the look, and
 * the block names the tier. The group's act goes to `previewTiles`.
 */
export function optionTile(o: OptionView, picture: string): Tile {
  const { id = '', ...data } = o.data;
  return {
    id, label: o.name, picture, data, sub: o.sub,
    mark: o.locked === '' ? undefined : PLAN_WORDS[o.locked],
  };
}

/** The picture of a UI option: the swatches of its tokens, or a glyph. */
export function swatchPic(swatches: readonly string[], glyph = ''): string {
  return swatches.length > 0
    ? `<span class="msw" aria-hidden="true">${swatches.map((c) => `<i style="background:${esc(c)}"></i>`).join('')}</span>`
    : `<span class="msw g" aria-hidden="true">${esc(glyph)}</span>`;
}

/** One row of a list with ▶: the press picks the option, and the block
 *  plays it. CUSTOM with no stored code is a row with ⌘ in every
 *  category (R2154), so `glyph` names the mark at its start. */
export function listRowHtml(o: OptionView, glyph = '▶'): string {
  const end = o.on ? `<em>${esc(S.in_use)}</em>` : mark(o);
  return `<button type="button" class="${cls('mlrow', o)}"${attrs(o)}>`
    + `<span class="mplay" aria-hidden="true">${esc(glyph)}</span>`
    + `<span class="mcn"><b>${esc(o.name)}</b>${o.sub === undefined ? '' : `<i>${esc(o.sub)}</i>`}</span>`
    + `${end}</button>`;
}

/** The id of the region of MIX BY CATEGORY. */
const MIX_ID = 'mods-mix';

/**
 * The fold MIX BY CATEGORY of THEMES (R2154): the disclosure row of the
 * SUPPORT question list (R1744), a button with `aria-expanded`, the lede
 * that invites a mix under it, open or closed, and under that the
 * category blocks while the fold stands open. A closed fold writes
 * no block, so no picture of a block is baked, and `aria-controls` names
 * the region only while the region is in the markup.
 */
export function mixFoldHtml(open: boolean, blocks: string): string {
  return `<div class="qa mmix${open ? ' on' : ''}">`
    + `<button type="button" class="qa-q" id="${MIX_ID}-q" data-act="mix" aria-expanded="${open}"`
    + `${open ? ` aria-controls="${MIX_ID}"` : ''}>`
    + `<span class="qa-t">${esc(S.mix_fold)}</span><span class="qa-c" aria-hidden="true"></span></button></div>`
    + `<div class="lede mmix-lede">${esc(S.mix_lede)}</div>`
    + (open ? `<div class="mthemes" id="${MIX_ID}" role="region" aria-labelledby="${MIX_ID}-q">${blocks}</div>` : '');
}

/** The axis under a scale of pictures. */
export function axisHtml(): string {
  return `<div class="maxis"><span>${esc(S.axis_quieter)}</span><span>${esc(S.axis_louder)}</span></div>`;
}

/** The head of one block: the name and the line, and the value in force.
 *  The head is a press that selects the block for the preview. */
export function headHtml(o: {
  act: string; data: string; sel: boolean; name: string; line: string; valueCls: string; value: string;
}): string {
  return `<div class="srow mrow mhead${o.sel ? ' sel' : ''}" data-act="${o.act}"${o.data}>`
    + `<span class="sk"><span class="sn">${esc(o.name)}</span><span class="sd">${esc(o.line)}</span></span>`
    + `<span class="val${o.valueCls}">${esc(o.value)}</span></div>`;
}

// --- the slot map --------------------------------------------------------------

/** One slot as the map draws it. `state` says what it wears: its own pick,
 *  the pick of its parent, or nothing. */
export interface SlotView {
  slot: string;
  label: string;
  state: 'own' | 'fol' | 'non';
  /** The name of what it wears, '' for nothing. */
  wears: string;
  on: boolean;
  wide: boolean;
}

export function slotHtml(v: SlotView): string {
  const value = v.state === 'own' ? esc(v.wears)
    : v.state === 'fol' ? esc(S.slot_follows({ name: v.wears }))
      : esc(S.slot_empty);
  return `<button type="button" class="mslot${v.on ? ' on' : ''}${v.wide ? ' wide' : ''}"`
    + ` data-act="esel" data-slot="${esc(v.slot)}" aria-pressed="${v.on}">`
    + `<b>${esc(v.label)}</b><i class="${v.state}">${value}</i></button>`;
}

/** The legend over the slot map. */
export function legendHtml(): string {
  return `<div class="mlegend"><span class="own">${esc(S.legend_own)}</span>`
    + `<span class="fol">${esc(S.legend_follow)}</span><span class="non">${esc(S.legend_none)}</span></div>`;
}

/** One group of the map: the head, the parent slot and its children. */
export function groupHtml(head: string, parent: string, kids: string): string {
  return `<div class="mgrp"><div class="mgh">${esc(head)}</div>${parent}`
    + (kids === '' ? '' : `<div class="mgrid">${kids}</div>`) + '</div>';
}

/** One row of WHAT FITS: a picture or a glyph, the name, a line and the
 *  end mark. */
export function fitRowHtml(o: {
  slot: string; code: string; on: boolean; locked: boolean; pic: string;
  name: string; line: string; end: string;
}): string {
  return `<button type="button" class="mfxr${o.on ? ' on' : ''}${o.locked ? ' lk' : ''}"`
    + ` data-act="ewear" data-slot="${esc(o.slot)}" data-code="${esc(o.code)}" aria-pressed="${o.on}">`
    + `${o.pic}<span class="mcn"><b>${esc(o.name)}</b><i>${esc(o.line)}</i></span>${o.end}</button>`;
}

/** The glyph box of a row with no picture: FOLLOW or NOTHING. */
export function glyphBox(glyph: string): string {
  return `<span class="mpic g">${esc(glyph)}</span>`;
}
